import React from "react";
import { PageBuilderBrochureLeadFormFragment } from "types/graphql-types";
import { useGeolocation } from "../../utils/localization/geolocation/GeolocationContext";
import { graphql } from "gatsby";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { isPresent } from "src/utils/checks";

import {
  useBrochureThankYouPageURL,
  useTargetBrochure
} from "src/utils/brochures";

import { BackgroundColor } from "types/componentBackground";
import { usePageType } from "./usePageType";
import { SectionContainer } from "@silversea-ssc/ui-components-react";
const BrochureLeadFormAsModal = React.lazy(
  () => import("components/BrochureLeadForm/BrochureLeadFormAsModal")
);
const BrochureLeadFormAsSticky = React.lazy(
  () => import("components/BrochureLeadForm/BrochureLeadFormAsSticky")
);
const BrochureLeadFormInline = React.lazy(
  () => import("components/BrochureLeadForm/BrochureLeadFormInline")
);

type PageBuilderBrochureLeadFormProps = PageBuilderBrochureLeadFormFragment & {
  backgroundColor: BackgroundColor;
};

const PageBuilderBrochureLeadForm: React.FC<
  React.PropsWithChildren<PageBuilderBrochureLeadFormProps>
> = ({
  heading,
  brochureGroup,
  asModal = false,
  asSticky = false,
  backgroundColor,
  ctaLabel,
  _key,
  formDisclaimerContent
}) => {
  const geolocation = useGeolocation();
  const language = useLanguage();
  const brochures = brochureGroup?.brochures?.filter(isPresent);
  const brochure = useTargetBrochure(brochures, geolocation, language);

  const pageType = usePageType();
  const thankYouPageURL = useBrochureThankYouPageURL(language, pageType);
  const openInNewPage = pageType == "LANDING_PAGE";

  const component = brochure ? (
    <React.Suspense>
      {asModal ? (
        <BrochureLeadFormAsModal
          heading={heading}
          brochure={brochure}
          backgroundColor={backgroundColor}
          openInNewPage={openInNewPage}
          thankYouPageURL={thankYouPageURL}
          ctaLabel={ctaLabel?.localized}
          dataContentName="brochure_lead_form"
          formDisclaimerContent={formDisclaimerContent}
        />
      ) : (
        <BrochureLeadFormInline
          heading={heading}
          brochure={brochure}
          thankYouPageURL={thankYouPageURL}
          openInNewPage={openInNewPage}
          ctaLabel={ctaLabel?.localized}
          formDisclaimerContent={formDisclaimerContent}
        />
      )}
    </React.Suspense>
  ) : null;

  const sectionBackgroundColor =
    backgroundColor == "titanium" ? "titaniumDark" : backgroundColor;

  return (
    <React.Suspense>
      {asSticky ? (
        <BrochureLeadFormAsSticky
          heading={heading}
          brochure={brochure}
          backgroundColor={sectionBackgroundColor}
          openInNewPage={openInNewPage}
          thankYouPageURL={thankYouPageURL}
          dataContentName="brochure_lead_form_sticky"
          ctaLabel={ctaLabel?.localized}
          dataPageBuilderAnchor={_key}
          formDisclaimerContent={formDisclaimerContent}
        >
          {component}
        </BrochureLeadFormAsSticky>
      ) : (
        <SectionContainer
          spacingTop="compact"
          spacingBottom="compact"
          backgroundColor={sectionBackgroundColor}
          dataContentName="brochure_lead_form"
          id={_key}
        >
          {component}
        </SectionContainer>
      )}
    </React.Suspense>
  );
};

export default PageBuilderBrochureLeadForm;

// eslint-disable-next-line import/no-unused-modules
export const fragments = graphql`
  fragment PageBuilderBrochureLeadForm_Brochure on SanityBrochure {
    thumbnail {
      ...ImageWithAltAndPreview
    }
    title
    brochureCode
    isActive
    language
    countries
  }

  fragment PageBuilderBrochureLeadForm on SanityBrochureLeadForm {
    _key
    sectionLayoutConfig {
      ...SectionLayoutConfig
    }
    heading {
      ...SectionTitle
      ...SectionTitleCTAs
    }
    brochureGroup {
      name
      brochures {
        ...PageBuilderBrochureLeadForm_Brochure
      }
    }
    ctaLabel {
      localized
    }
    asModal
    asSticky
    formDisclaimerContent: formDisclaimer {
      ...FormDisclaimer
    }
  }
`;
